import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FiMenu, FiX } from "react-icons/fi";
import AddButton from "../../components/button/AddButton";
import CandidateFilters from "../../components/filter/Filter";
import { PAGE_SIZE, LOAD_MORE } from "../../config/constants";
import useIsDesktop from "../../hooks/useIsDesktop";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import Pagination from "../../components/pagination/Pagination";
import ConfirmModal from "../../components/modal/ConfirmModal";
import Toast from "../../components/toast/Toast";
import { getErrorToastMessage } from "../../utils/httpToast";
import CandidateRow from "./components/CandidateRow";
import CandidateService from "../../services/candidateService";

const CandidatePage = () => {
  const { t } = useTranslation();
  const candidateService = useMemo(() => CandidateService(), []);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const[isLoading, setIsLoading] = useState(true);

  const filtersFromUrl = {
    firstName: searchParams.get("firstName") || "",
    currentPosition: searchParams.get("currentPosition") || "",
    city: searchParams.getAll("city"),
    degreeTitle: searchParams.get("degreeTitle") || "",
  };

  const [draftFilters, setDraftFilters] = useState(filtersFromUrl);
  const [appliedFilters, setAppliedFilters] = useState(filtersFromUrl);

  const isDesktop = useIsDesktop();

  const initialPageFromUrl = (() => {
    const p = Number(searchParams.get("page"));
    return Number.isFinite(p) && p >= 1 ? p : 1;
  })();

  const [page, setPage] = useState(initialPageFromUrl);

  const initialVisibleCount = (() => {
    const v = Number(searchParams.get("visible"));
    return Number.isFinite(v) && v >= PAGE_SIZE ? v : PAGE_SIZE;
  })();

  const [visibleCount, setVisibleCount] = useState(initialVisibleCount);
  const scrollYFromUrl = (() => {
    const v = Number(searchParams.get("scrollY"));
    return Number.isFinite(v) && v > 0 ? v : 0;
  })();

  const scrollRestoredRef = useRef(false);
  const isMountedRef = useRef(false);

  const [candidates, setCandidates] = useState([]);
  const [serverTotalPages, setServerTotalPages] = useState(null);
  const [totalElements, setTotalElements] = useState(0);

  const [candidateToDelete, setCandidateToDelete] = useState(null);
  const [toast, setToast] = useState({ open: false, type: "success", message: "" });
  const showToast = (type, message) => {
    setToast({ open: true, type, message });
  };

  const [availableCities, setAvailableCities] = useState([]);

  const [searchKey, setSearchKey] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const flashToast = location.state?.toast;
    if (!flashToast) return;

    setToast({
      open: true,
      type: flashToast.type,
      message: flashToast.message,
    });

    navigate(`${location.pathname}${location.search || ""}`, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  const handleFilterChange = (name, value) =>
      setDraftFilters((prev) => ({ ...prev, [name]: value }));

  const handleAddCandidate = () => {
    navigate("/add-candidate");
  };

  const handleCityToggle = (cityName) => {
    setDraftFilters(prev => ({
      ...prev,
      city: prev.city.includes(cityName)
        ? prev.city.filter(c => c !== cityName)
        : [...prev.city, cityName]
    }));
  };

  useEffect(() => {
    if (!isDesktop) return;
    const p = Number(searchParams.get("page"));
    const urlPage = Number.isFinite(p) && p >= 1 ? p : 1;
    setPage(urlPage);
  }, [isDesktop, searchParams]);

  useEffect(() => {
    if (!isDesktop) {
      if (!isMountedRef.current) {
        isMountedRef.current = true;
        return;
      }
      const params = new URLSearchParams(searchParams);
      params.delete("visible");
      setSearchParams(params, { replace: true });
    }
  }, [isDesktop]);

  useEffect(() => {
    const controller = new AbortController();

    const loadCities = async () => {
      try {
        const data = await candidateService.listFilterCities({ signal: controller.signal });
        const cities = Array.isArray(data) ? data : [];
        setAvailableCities(cities);
      } catch (err) {
        if (err.name === 'CanceledError') return;
        setAvailableCities([]);
        showToast("error", getErrorToastMessage(err, t));
      }
    };

    loadCities();
    return () => controller.abort();
  }, [candidateService]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchList = async () => {
      setIsLoading(true);
      try {
        const apiPage = isDesktop ? (page - 1) : 0;
        const apiSize = isDesktop ? PAGE_SIZE : visibleCount;

        const apiFilters = {
          firstName: appliedFilters.firstName || undefined,
          currentPosition: appliedFilters.currentPosition || undefined,
          degreeTitle: appliedFilters.degreeTitle || undefined,
          cities: appliedFilters.city?.length ? appliedFilters.city : undefined
        };

        const data = await candidateService.list({ page: apiPage, pageSize: apiSize, filters: apiFilters, signal: controller.signal });

        const mapped = (data?.listCandidates ?? []).map((c) => ({
          ...c,
          degrees: c.educationTitle ? [c.educationTitle] : c.degrees,
        }));

        setCandidates(mapped);
        setServerTotalPages(Math.max(1, Number(data?.totalPages ?? 1)));
        setTotalElements(Number(data?.totalElements ?? 0));
      } catch (err) {
        if (err.name === 'CanceledError') return;
        showToast("error", getErrorToastMessage(err, t));
      } finally {
        setIsLoading(false);
      }
    };

    fetchList();
    return () => controller.abort();
  }, [candidateService, isDesktop, page, visibleCount, searchKey, reloadKey, appliedFilters, t]);

  useEffect(() => {
    if (!isDesktop) return;
    if (serverTotalPages === null) return;
    setPage((p) => Math.min(p, serverTotalPages));
  }, [serverTotalPages, isDesktop]);

  const displayedCandidates = useMemo(() => candidates, [candidates]);
  const hasMoreMobile = !isDesktop && visibleCount < totalElements;

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + LOAD_MORE);
  }, []);

  const { sentinelRef, resetLoading } = useInfiniteScroll({
    enabled: !isDesktop && !isLoading,
    hasMore: hasMoreMobile,
    onLoadMore: handleLoadMore,
  });

  useEffect(() => {
    if (!isLoading) resetLoading();
  }, [isLoading, resetLoading]);

  useEffect(() => {
    if (isLoading) return;
    if (scrollRestoredRef.current) return;
    if (scrollYFromUrl <= 0) return;
    scrollRestoredRef.current = true;
    requestAnimationFrame(() => window.scrollTo({ top: scrollYFromUrl, behavior: "instant" }));
  }, [isLoading, scrollYFromUrl]);

  useEffect(() => {
    if (isDesktop) return;
    if (visibleCount === PAGE_SIZE) return;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("visible", String(visibleCount));
      return params;
    }, { replace: true });
  }, [visibleCount]);

  const onEdit = (id) => {
    const scrollY = window.scrollY;
    const currentParams = new URLSearchParams(location.search);
    currentParams.delete("scrollY");
    const search = currentParams.toString() ? `?${currentParams.toString()}` : "";
    const base = `${location.pathname}${search}`;
    const sep = base.includes("?") ? "&" : "?";
    const returnUrl = encodeURIComponent(`${base}${sep}scrollY=${scrollY}`);
    navigate(`/edit-candidate/${id}?returnUrl=${returnUrl}`);
  };

  const onDelete = (candidate) => {
   setCandidateToDelete(candidate);
  };

  const confirmDelete = async () => {
    if (!candidateToDelete) return;

    try {
      await candidateService.remove(candidateToDelete.id);
      showToast("success", t("MODAL.SUCCESSFUL"));
      setReloadKey((k) => k + 1);
    } catch (err) {
      showToast("error", getErrorToastMessage(err, t));
    } finally {
      setCandidateToDelete(null);
    }
  };

  const onSearch = () => {
    setAppliedFilters(draftFilters);
    const params = new URLSearchParams();
    if (draftFilters.firstName) params.set("firstName", draftFilters.firstName);
    if (draftFilters.currentPosition) params.set("currentPosition", draftFilters.currentPosition);
    if (draftFilters.degreeTitle) params.set("degreeTitle", draftFilters.degreeTitle);
    draftFilters.city?.forEach((c) => params.append("city", c));
    if (isDesktop) {
      params.set("page", "1");
      setPage(1);
    }
    setSearchParams(params, { replace: true });
    setVisibleCount(PAGE_SIZE);
    setSearchKey((k) => k + 1);
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 mt-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-6 py-3">
        <div className="flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-10 mb-8">
          <div className="flex sm:hidden items-center justify-between gap-4">
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center gap-2 px-4 h-10 border border-secondary rounded-xl text-sm font-semibold bg-white"
            >
              {showMobileFilters ? <FiX size={20} /> : <FiMenu size={20} />}
              {showMobileFilters ? t("FILTER.CLOSE") : t("FILTER.FILTERS")}
            </button>

            <AddButton onClick={handleAddCandidate}>{t("CANDIDATES.ADD")}</AddButton>
          </div>

          <div className={`flex-1 ${showMobileFilters ? "block" : "hidden sm:block"}`}>
            <CandidateFilters
              filters={draftFilters}
              onFilterChange={handleFilterChange}
              onCityToggle={handleCityToggle}
              availableCities={availableCities}
              onSearch={onSearch}
              t={t}
            />
          </div>

          <div className="hidden sm:block flex-shrink-0">
            <AddButton onClick={handleAddCandidate}>{t("CANDIDATES.ADD")}</AddButton>
          </div>
        </div>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">{t("NAVBAR.CANDIDATES")}</h1>
        </div>

      <ul className="space-y-3">
        {isLoading ? (
            Array.from({ length: 5 }).map((_, idx) => (
              <CandidateRow key={`skeleton-${idx}`} isLoading={true} />
            ))
        ) : displayedCandidates.length === 0 ? (
          <li className="py-10">
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900">
                {t("CANDIDATES.NO_RESULTS_TITLE")}
              </p>
            </div>
          </li>
        ) : (
        displayedCandidates.map((c) => (
            <CandidateRow
                key={c.id}
                candidate={c}
                onEdit={onEdit}
                onDelete={onDelete}
            />
        ))
      )}
      </ul>

        {!isDesktop && (
          <>
            <div ref={sentinelRef} className="h-10" />
            {hasMoreMobile && (
              <p className="mt-2 text-center text-sm text-mediumGray">
                {t("ACTIONS.LOADING_MORE")}
              </p>
            )}
          </>
        )}

        {isDesktop && (serverTotalPages ?? 0) > 1 && !isLoading && (
          <Pagination
            page={page}
            totalPages={serverTotalPages ?? 1}
            onChange={(p) => {
              const params = new URLSearchParams(searchParams);
              params.set("page", String(p));
              setSearchParams(params, { replace: true });
              setPage(p);
            }}
            maxButtons={5}
          />
        )}
      </div>

      <ConfirmModal
        isOpen={!!candidateToDelete}
        onClose={() => setCandidateToDelete(null)}
        onConfirm={confirmDelete}
        title={t("CANDIDATES.DELETE")}
        description={t("MODAL.SAFE_REMOVE_CANDIDATE")}
        cancelLabel={t("ACTIONS.CANCEL")}
        confirmLabel={t("MODAL.REMOVE")}
        confirmClassName="bg-red text-white hover:opacity-90"
      />

      <Toast
        open={toast.open}
        variant={toast.type}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default CandidatePage;